import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useHydra } from './hooks/useHydra';
import { useKalshiAccount } from './hooks/useKalshiAccount';
import { usePolyAccount } from './hooks/usePolyAccount';
import { playBootTone } from './lib/audio/defenseAudio';
import { readSoundSettings } from './lib/audio/soundSettings';
import { AppSidebar } from './components/layout/AppSidebar';
import { ValueDashboard } from './views/ValueDashboard';
import { PositionTracker } from './views/PositionTracker';
import { Settings } from './views/Settings';
import { StreamRiftView } from './views/StreamRift';
import type { AppView } from './types/navigation';

const G3View = lazy(() => import('./views/G3'));

function App() {
  useHydra(); // Market data WS — persists across view switches
  useKalshiAccount(); // Kalshi account — auto-connects if saved credentials exist
  usePolyAccount(); // Polymarket account — auto-connects if saved config exists
  const [currentView, setCurrentView] = useState<AppView>('dashboard');

  // Boot tone — plays once on first mount if enabled
  const bootPlayed = useRef(false);
  useEffect(() => {
    if (!bootPlayed.current) {
      bootPlayed.current = true;
      const snd = readSoundSettings();
      if (snd.bootSoundEnabled) playBootTone();
    }
  }, []);

  return (
    <div className="bg-background text-foreground flex h-screen overflow-hidden">
      <aside className="bg-muted border-border/50 w-16 border-r">
        <AppSidebar currentView={currentView} onViewChange={setCurrentView} />
      </aside>
      <main className="flex-1 overflow-hidden">
        {currentView === 'dashboard' && <ValueDashboard />}
        {currentView === 'positions' && <PositionTracker />}
        {currentView === 'settings' && <Settings />}
        {currentView === 'streamrift' && <StreamRiftView />}
        {currentView === 'g3' && (
          <Suspense
            fallback={
              <div className="text-muted-foreground flex h-full items-center justify-center text-xs">
                Loading G3…
              </div>
            }
          >
            <G3View />
          </Suspense>
        )}
      </main>
    </div>
  );
}

export default App;
